import { theme, Button, Segmented } from 'antd'
import { SunOutlined, MoonOutlined, EyeOutlined } from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { Logo } from './Logo'
import { useThemeContext } from '../../context/theme'
import { StyledHeader, HeaderContent, LeftSection, RightSection } from './styles/AppHeaderStyles'
import { COLORS, MEDIA, TYPOGRAPHY, FONT_WEIGHT } from '../../styles/theme'

const { useToken } = theme

const CenterSection = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${COLORS.textSecondary};
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.medium};

  .anticon {
    font-size: ${TYPOGRAPHY.caption.fontSize};
  }

  ${MEDIA.mobile} {
    span:not(.anticon) {
      display: none;
    }
  }
`

/**
 * Simplified header for public pages (view-only mode)
 * Shows logo, read-only indicator, theme toggle, and login button
 */
interface PublicHeaderProps {
  showReadOnlyIndicator?: boolean
}

export function PublicHeader({ showReadOnlyIndicator = true }: PublicHeaderProps) {
  const { token } = useToken()
  const navigate = useNavigate()
  const { theme: currentTheme, setTheme } = useThemeContext()

  const handleThemeChange = (value: string | number) => {
    setTheme(value as 'light' | 'dark')
  }

  return (
    <StyledHeader $background={token.colorBgContainer} $borderColor={token.colorBorderSecondary}>
      <HeaderContent align="center" justify="space-between">
        {/* Left side - Logo only */}
        <LeftSection align="center">
          <Logo />
        </LeftSection>

        {/* Center - Read-only indicator */}
        {showReadOnlyIndicator && (
          <CenterSection>
            <EyeOutlined />
            <span>You are viewing this project in read-only mode</span>
          </CenterSection>
        )}

        {/* Right side - Theme toggle and Login button */}
        <RightSection align="center">
          <Segmented
            value={currentTheme}
            onChange={handleThemeChange}
            options={[
              {
                value: 'light',
                icon: <SunOutlined />,
              },
              {
                value: 'dark',
                icon: <MoonOutlined />,
              },
            ]}
            size="small"
          />
          <Button type="primary" onClick={() => navigate('/login')}>
            Log in
          </Button>
        </RightSection>
      </HeaderContent>
    </StyledHeader>
  )
}
